import {ReactComponent as UploadIcon} from "../../img/icon/upload.svg";
import {ReactComponent as PdfIcon} from "../../img/icon/pdf.svg";

import {getFileMimeType, verify} from "./InputFile";
import {useEffect, useRef, useState} from "react";
import {showError} from "../../js/main";
import {isPdfFilename} from "../../js/util";

export const IMG_TARGET_TYPE = 'image/jpeg';
export const PDF_TARGET_TYPE = "application/pdf";

export const normalizeFile = async (file) => {
    const getOutputName = (originalName, fallbackName, extension) => {
        const base = originalName?.replace(/\.[^/.]+$/, "") || fallbackName;
        return `${base}.${extension}`;
    };

    const toImageFile = (blob, originalFile) =>
        new File([blob], getOutputName(originalFile.name, "image", "jpg"), {
            type: IMG_TARGET_TYPE,
            lastModified: originalFile.lastModified || Date.now(),
        });

    const encodeViaCanvas = (bitmap) => {
        const canvas = document.createElement("canvas");
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const context = canvas.getContext("2d");
        if (!context) {
            return Promise.reject(new Error("Canvas context unavailable"));
        }
        context.drawImage(bitmap, 0, 0);
        return new Promise((resolve, reject) =>
            canvas.toBlob(
                (blob) => (blob ? resolve(blob) : reject(new Error("Canvas encoding failed"))),
                IMG_TARGET_TYPE,
                0.85
            )
        );
    };

    if(!file){
        return null
    }

    const mime = getFileMimeType(file);

    if (mime === PDF_TARGET_TYPE) {
        const normalizedName = getOutputName(file.name, "document", "pdf");
        if (file instanceof File &&
            file.type.toLowerCase() === PDF_TARGET_TYPE &&
            isPdfFilename(file.name)) {
            return file;
        }

        return new File([file], normalizedName, {
            type: PDF_TARGET_TYPE,
            lastModified: file.lastModified || Date.now(),
        });
    }

    if (mime === IMG_TARGET_TYPE && file instanceof File) {
        return file;
    }

    const isHeic =
        mime === "image/heic" ||
        mime === "image/heif" ||
        file.name?.toLowerCase().endsWith(".heic") ||
        file.name?.toLowerCase().endsWith(".heif");

    if (!mime.startsWith("image/") && !isHeic) {
        showError("Das Dateiformat wird leider nicht unterstützt.")
        throw new Error("Unsupported file type");
    }

    let bitmap;
    try {
        bitmap = await createImageBitmap(file);
    } catch {
        showError("Das Dateiformat wird leider nicht unterstützt. Bitte verwenden Sie bevorzugt .jpg/.jpeg")
        throw new Error(
            isHeic
                ? "HEIC images are not supported in this browser. Please convert to JPEG before uploading."
                : "Could not decode image."
        );
    }

    try {
        const blob = await encodeViaCanvas(bitmap);
        return toImageFile(blob, file);
    } finally {
        bitmap.close();
    }
};

export const InputFileFancy = ({
                                    name,
                                    label,
                                    setter,
                                    maxMBSize = -1,
                                    accept = "image/*",
                                    required = false,
                                    disabled = false,
                                    concatId = null,
                                    initialFile = null,
                                    onClick = (e) => {
                                    },
                                    uniqueRef = null,
                                    innerRef = null
                                }) => {

    const identifier = concatId ? name + concatId : name;
    const inputTagRef = useRef();
    const fileChanged = useRef(() => {
    });
    const previewVersion = useRef(0);
    const [dataUrl, setDataUrl] = useState();
    const [isPdf, setIsPdf] = useState(false);

    const releaseUniqueReference = () => {
        fileChanged.current();
        fileChanged.current = () => {};
    };

    useEffect(() => () => releaseUniqueReference(), []);

    const clearPreview = () => {
        previewVersion.current += 1;
        setDataUrl(null);
        setIsPdf(false);
    };

    const clearSelection = () => {
        releaseUniqueReference();
        if (inputTagRef.current) {
            inputTagRef.current.value = null;
        }
        clearPreview();
        setter(null, name);
    };

    const setPreview = (file) => {
        const version = ++previewVersion.current;
        if (file.type === PDF_TARGET_TYPE) {
            setDataUrl(null);
            setIsPdf(true);
            return;
        }

        setIsPdf(false);
        const reader = new FileReader();
        reader.onload = (event) => {
            if (previewVersion.current === version) {
                setDataUrl(event.target.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const getFileHash = async (file) => {
        const buff = await file.arrayBuffer();
        const hash = await crypto.subtle.digest("SHA-256", buff);
        return Array.from(
            new Uint8Array(hash),
            byte => byte.toString(16).padStart(2, "0")
        ).join("");
    };

    useEffect(() => {
        if (!initialFile) {
            clearPreview();
            return;
        }

        let canceled = false;
        releaseUniqueReference();
        setPreview(initialFile);

        if (uniqueRef) {
            getFileHash(initialFile).then(hash => {
                if (canceled) {
                    return;
                }
                uniqueRef.current.add(hash);
                fileChanged.current = () => uniqueRef.current.delete(hash);
            }).catch(() => {
            });
        }

        return () => {
            canceled = true;
        };
    }, [initialFile]); // eslint-disable-line react-hooks/exhaustive-deps

    const fileSelected = async (e) => {
        let file;
        try {
            file = await verify(accept, maxMBSize, e);
        } catch {
            clearSelection();
            return;
        }

        if (!file) {
            clearSelection();
            return;
        }

        releaseUniqueReference();

        let hash;
        if (uniqueRef) {
            try {
                hash = await getFileHash(file);
            } catch {
                showError("Die Datei konnte nicht verarbeitet werden.");
                clearSelection();
                return;
            }

            if (uniqueRef.current.has(hash)) {
                showError("Die gleiche Datei kann nicht doppelt gewählt werden.");
                clearSelection();
                return;
            }
        }

        let normalizedFile;
        try {
            normalizedFile = await normalizeFile(file);
        } catch {
            clearSelection();
            return;
        }

        if (uniqueRef) {
            uniqueRef.current.add(hash);
            fileChanged.current = () => uniqueRef.current.delete(hash);
        }

        setter(normalizedFile, name);
        setPreview(normalizedFile);
    };

    const setInputRef = (node) => {
        inputTagRef.current = node;
        if (typeof innerRef === "function") {
            innerRef(node);
        } else if (innerRef) {
            innerRef.current = node;
        }
    }

    return (
        <div className="form-input" onClick={onClick}>
            {label &&
                <label htmlFor={identifier}>
                    {label} {required && <span className="primary-link bold">*</span>}
                </label>}
            <div className="image-input-container">
                <div>
                    <button type="button" className="image-input">
                        <label htmlFor={identifier}>
                            {!dataUrl && !isPdf && <>
                                <UploadIcon className="file-svg file-svg-center"/>
                            </>}
                            {dataUrl && <img src={dataUrl} className="preview-img" alt="Dateivorschau"/>}
                            {isPdf && <PdfIcon className="pdf-svg file-svg-center"/>}
                            <span>{initialFile ? "Datei ändern" : "Datei hochladen"}</span>

                            <input id={identifier}
                                   type="file"
                                   name={name}
                                   onChange={fileSelected}
                                   disabled={disabled}
                                   required={required && !initialFile}
                                   accept={accept}
                                   ref={setInputRef}/>
                        </label>
                    </button>

                </div>
            </div>
        </div>
)
}
